/**
 * App Routes Component
 * Generates routes from route configuration
 * @module components/AppRoutes
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { routeConfig, getDefaultRoute, hasRouteAccess } from './routes';
import ProtectedRoute from './ProtectedRoute';
import RoleBasedRoute from './RoleBasedRoute';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { normalizeRole } from '@/shared/lib/roleUtils';
import { useTranslation } from '@/shared/i18n/useTranslation';
import Loading from '@/shared/components/Loading';
import { getLoadingText } from '@/utils/getLoadingText';

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Loading fullPage text={getLoadingText()} size="lg" color="primary" />
);

const loginRedirectPath = (pathname, search = '') => {
  const returnUrl = `${pathname}${search}`;
  if (!returnUrl || returnUrl === '/' || returnUrl.startsWith('/login')) {
    return '/login';
  }
  return `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
};

const NotFoundPage = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        {t('common.pageNotFoundTitle')}
      </h2>
      <p className="text-gray-600 dark:text-gray-300">
        {t('common.pageNotFoundMessage')}
      </p>
    </div>
  );
};

// Component to redirect employee page to default tab
const EmployeeRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/employees/${id}/overview`} replace />;
};

/**
 * AppRoutes - Generates routes from configuration
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 * @param {Function} props.onLogin - Login handler
 * @returns {React.Component} - Routes component
 */
const AppRoutes = ({ user, onLogout, onLogin }) => {
  const location = useLocation();

  const renderRoute = (route) => {
    const { 
      path, 
      component: Component, 
      roles = [], 
      requiresAuth = false, 
      public: isPublic = false,
      lazy = false,
      redirectTo,
      props: componentProps = {}
    } = route;

    // Handle redirects - create a redirect component
    if (redirectTo) {
      const RedirectComponent = () => {
        const params = useParams();
        const redirectPath = redirectTo.replace(':id', params.id || '');
        return <Navigate to={redirectPath} replace />;
      };
      return (
        <Route
          key={path}
          path={path}
          element={<RedirectComponent />}
        />
      );
    }

    // Public routes - all are lazy loaded now
    if (isPublic) {
      // Landing and privacy (and forget password): always show page - allow direct link access
      const alwaysShowPaths = ['/', '/privacy', '/delete-account', '/ForgetPassword', '/support-session'];
      if (alwaysShowPaths.includes(path)) {
        return (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Component {...(path === '/login' && onLogin ? { onLogin } : {})} />
              </Suspense>
            }
          />
        );
      }
      // Login page: redirect to dashboard if already logged in
      return (
        <Route
          key={path}
          path={path}
          element={
            user ? (
              <Navigate to={getDefaultRoute(normalizeRole(user.role))} replace />
            ) : (
              <Suspense fallback={<LoadingFallback />}>
                <Component {...(path === '/login' && onLogin ? { onLogin } : {})} />
              </Suspense>
            )
          }
        />
      );
    }

    // Protected routes - all are lazy loaded now
    if (requiresAuth) {
      const routeElement = (
        <ProtectedRoute user={user}>
          <RoleBasedRoute user={user} allowedRoles={roles} path={path}>
            <ErrorBoundary key={location.pathname} resetKeys={[location.pathname]}>
              <Suspense fallback={<LoadingFallback />}>
                <Component user={user} {...componentProps} />
              </Suspense>
            </ErrorBoundary>
          </RoleBasedRoute>
        </ProtectedRoute>
      );

      return (
        <Route
          key={path}
          path={path}
          element={routeElement}
        />
      );
    }

    // Default: render without protection (all lazy loaded)
    return (
      <Route
        key={path}
        path={path}
        element={
          <Suspense fallback={<LoadingFallback />}>
            <Component {...componentProps} />
          </Suspense>
        }
      />
    );
  };

  return (
    <Routes>
      {routeConfig.map(renderRoute)}
      
      {/* Special redirect route for /employees/:id */}
      <Route
        path="/employees/:id"
        element={
          user ? (
            hasRouteAccess('/employees', user) ? (
              <EmployeeRedirect />
            ) : (
              <Navigate to={getDefaultRoute(user.role)} replace />
            )
          ) : (
            <Navigate to={loginRedirectPath(location.pathname, location.search)} replace />
          )
        }
      />

      {/* 404 route */}
      <Route
        path="*"
        element={
          !user ? (
            <Navigate to={loginRedirectPath(location.pathname, location.search)} replace />
          ) : (
            <NotFoundPage />
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;

